import { Inject } from "@angular/core";
import { ANGULARJS_TRANSLATE } from "@nms-ng2/app/shared/services/upgraded-provider/upgraded-providers";
import {
    AmplifierDirectionMap,
    CircuitElementState,
    MuxElement,
    OscElement,
    TransponderElement
} from "../models/diagram.models";
import { DwdmDiagramConverterHelper } from "./dwdm-diagram-converter-helper";
import { DwdmDiagramConverterModelsBuilder } from "./dwdm-diagram-converter-models-builder";
import {
    AmplifiersConverterData,
    EndConverterData,
    EndpointElementIdentifier,
    MuxConverterData,
    OscsConverterData,
    TranspondersConverterData
} from "./dwdm-diagram-converter.models";
import { CutStringPipe } from "@nms-ng2/app/shared/pipes/cut-string.pipe";

/**
 * Classe abstrata para realizar a conversão de @see EndConverterData em @see EndpointDeviceElement.
 * Pode ser usado tanto para o A-End quanto para o Z-End.
 *
 * Essa classe contém todos os comportamentos comuns entre todas as janelas.
 * Comportamentos específicos da tela de Diagnósticos ou da Wizard de criação devem ser
 * implementados nas classes concretas.
 */
export abstract class DwdmDiagramAbstractConverterEnd {

    private readonly END_NAME_MAX_CHARS = 30;

    private readonly converterModelsBuilder: DwdmDiagramConverterModelsBuilder;
    private readonly cutStringPipe: CutStringPipe;

    constructor(@Inject(ANGULARJS_TRANSLATE) private readonly translate: any) {
        this.converterModelsBuilder = new DwdmDiagramConverterModelsBuilder();
        this.cutStringPipe = new CutStringPipe();
    }

    protected convertData(
            endConverterData: EndConverterData,
            converters: {
                transponders: (data: TranspondersConverterData) => TransponderElement[];
                mux: (data: MuxConverterData) => MuxElement;
                amplifiers: (data: AmplifiersConverterData) => AmplifierDirectionMap;
                oscs: (data: OscsConverterData) => OscElement;
            }): EndpointElementIdentifier {

        const deviceResourceId = endConverterData.end?.id;
        const endpointType = endConverterData.endpointType;
        const { deviceName, tooltip, state } = this.getEndState(endConverterData);

        const transpondersData = this.converterModelsBuilder.buildTranspondersConverterData(endConverterData);
        const transponders = converters.transponders(transpondersData);

        const muxData = this.converterModelsBuilder.buildMuxConverterData(endConverterData);
        const mux = converters.mux(muxData);

        const amplifiersData = this.converterModelsBuilder.buildAmplifiersConverterData(endConverterData);
        const amplifiers = converters.amplifiers(amplifiersData);

        const oscsData = this.converterModelsBuilder.buildOscsConverterData(endConverterData);
        const osc = converters.oscs(oscsData);

        const showInternalLinks = this.shouldShowInternalLinks(endConverterData, mux);

        return {
            deviceResourceId,
            endpoint: {
                id: DwdmDiagramConverterHelper.createId(),
                name: deviceName,
                endpointType,
                state,
                transponders,
                mux,
                amplifiers,
                osc,
                showInternalLinks,
                getCustomTooltip: () => tooltip
            }
        };
    }

    private getEndState(endConverterData: EndConverterData) {
        const endpointType = endConverterData.endpointType;
        let deviceName = this.translate.instant(`dwdm.circuit.diagram.end.initial.name.${endpointType}`);
        let state = CircuitElementState.INITIAL;
        let fullName = deviceName;

        if (!this.isInitialState(endConverterData)) {
            fullName = endConverterData.end.name;
            deviceName = this.cutStringPipe.transform(fullName, this.END_NAME_MAX_CHARS);
            state = CircuitElementState.CONFIGURED;
        }

        return { deviceName, tooltip: fullName, state };
    }

     /**
     * Os links internos dos equipamentos Endpoint passam a ser exibidos
     * quando uma porta for selecionada, ou quando o Mux estiver presente.
     *
     * Configurações com mais de uma porta precisam ter um Mux,
     * assim se houver mais de uma porta configurada sem Mux no equipamento,
     * os links internos não são exibidos, visto que é uma configuração que não faz sentido.
     */
    private shouldShowInternalLinks(endConverterData: EndConverterData, mux: MuxElement): boolean {
        return endConverterData.ports.length === 1 || mux != null;
    }

    /**
     * Caso o end ainda não tenha sido selecionado pelo usuário, consideramos que o diagrama está em seu estado inicial.
     */
    private isInitialState(endConverterData: EndConverterData): boolean {
        return !endConverterData.end;
    }
}
